import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import SalarySubNav from './SalarySubNav';
import { apiGet, apiSend } from './api';
import { fmtCurrency } from './formatters';
import type { TaxBracket } from './types';
import DataTable from '../../shared/components/DataTable';

interface Draft {
  min_gross: string;
  max_gross: string;
  rate: string; // decimal, e.g., 0.06
  bracket_basis: 'per_period' | 'annualized';
  notes: string;
}

const TaxBrackets: React.FC = () => {
  const { sessionToken } = useAuth();
  const [brackets, setBrackets] = useState<TaxBracket[]>([]);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const reload = useCallback(async () => {
    try {
      const res = await apiGet<{ brackets: TaxBracket[] }>('/tax-brackets', sessionToken);
      setBrackets(res.brackets);
      setDrafts(res.brackets.map((b) => ({
        min_gross: String(b.min_gross),
        max_gross: b.max_gross === null ? '' : String(b.max_gross),
        rate: String(b.rate),
        bracket_basis: b.bracket_basis,
        notes: b.notes || '',
      })));
    } catch (e: any) { setErr(e.message); }
  }, [sessionToken]);

  useEffect(() => { reload(); }, [reload]);

  const updateDraft = (idx: number, patch: Partial<Draft>) => {
    setDrafts(drafts.map((d, i) => (i === idx ? { ...d, ...patch } : d)));
  };
  const addRow = () => setDrafts([...drafts, { min_gross: '0', max_gross: '', rate: '0', bracket_basis: 'per_period', notes: '' }]);
  const removeRow = (idx: number) => setDrafts(drafts.filter((_, i) => i !== idx));

  const save = async () => {
    try {
      setErr(''); setMsg('');
      const payload = drafts.map((d) => ({
        min_gross: Number(d.min_gross),
        max_gross: d.max_gross === '' ? null : Number(d.max_gross),
        rate: Number(d.rate),
        bracket_basis: d.bracket_basis,
        notes: d.notes || null,
      }));
      await apiSend('/tax-brackets', 'PUT', { brackets: payload, effective_from: effectiveFrom }, sessionToken);
      setMsg('Brackets saved. Run "Recalculate" on open periods to apply the new rates.');
      reload();
    } catch (e: any) { setErr(e.message); }
  };

  return (
    <div className="salary-module">
      <h2>Tax Brackets</h2>
      <SalarySubNav />
      {err && <div className="error-banner">{err}</div>}
      {msg && <div className="error-banner" style={{ background: '#d4edda', color: '#155724' }}>{msg}</div>}

      <div className="salary-card">
        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
          <strong>Active set:</strong> {brackets.length} bracket(s). Rates apply progressively — each
          bracket's rate covers only the portion of gross within its [min, max] range. Rate is a
          decimal (0.06 = 6%). Leave "max" empty for the top open-ended bracket.
        </p>
      </div>

      <div className="salary-card">
        <DataTable
          rows={drafts}
          rowKey={(_d, i) => i}
          caption="Tax brackets"
          moreLabel="Basis & notes"
          empty="No brackets defined."
          columns={[
            { key: 'label', header: 'Bracket', priority: 'primary', render: (_d, i) => `Bracket ${i + 1}` },
            {
              key: 'min', header: 'Min gross', priority: 'summary', numeric: true,
              render: (d, i) => <input type="number" step="0.01" aria-label="Min gross" value={d.min_gross} onChange={(e) => updateDraft(i, { min_gross: e.target.value })} />,
            },
            {
              key: 'max', header: 'Max gross (blank = ∞)', priority: 'summary', numeric: true,
              render: (d, i) => <input type="number" step="0.01" aria-label="Max gross" value={d.max_gross} onChange={(e) => updateDraft(i, { max_gross: e.target.value })} placeholder="(open)" />,
            },
            {
              key: 'rate', header: 'Rate (decimal)', priority: 'summary', numeric: true,
              render: (d, i) => (
                <>
                  <input type="number" step="0.0001" aria-label="Rate" value={d.rate} onChange={(e) => updateDraft(i, { rate: e.target.value })} />
                  <small style={{ color: 'var(--cs-ink-2)' }}> = {(Number(d.rate) * 100).toFixed(2)}%</small>
                </>
              ),
            },
            {
              key: 'basis', header: 'Basis', priority: 'detail',
              render: (d, i) => (
                <select aria-label="Basis" value={d.bracket_basis} onChange={(e) => updateDraft(i, { bracket_basis: e.target.value as any })}>
                  <option value="per_period">per period</option>
                  <option value="annualized">annualized</option>
                </select>
              ),
            },
            { key: 'notes', header: 'Notes', priority: 'detail', render: (d, i) => <input aria-label="Notes" value={d.notes} onChange={(e) => updateDraft(i, { notes: e.target.value })} /> },
          ]}
          actions={(_d, i) => <button className="salary-btn secondary" onClick={() => removeRow(i)}>✕ Remove</button>}
        />

        <div className="salary-actions" style={{ marginTop: '1rem' }}>
          <button className="salary-btn secondary" onClick={addRow}>+ Add Bracket</button>
          <label style={{ marginLeft: '1rem' }}>Effective from: </label>
          <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          <button className="salary-btn" onClick={save}>Save Bracket Set</button>
        </div>

        <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.75rem' }}>
          Sample verification: $1,056 gross with current placeholder brackets (0/6/11%) →
          tax applied on amount above $250 at 6% = {fmtCurrency((1056 - 250) * 0.06)}.
        </p>
      </div>
    </div>
  );
};

export default TaxBrackets;
